import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';

/**
 * Deliberately named `UserNotification`, not `Notification`: tsconfig.base.json pulls "dom"
 * into `lib`, so `Notification` is a global type. A module-local class of the same name is
 * legal but a reliable trap.
 *
 * A notification is always addressed to an *internal* user by their `AdornisUser._id` — there
 * is no field for a free-form address, so a customer can never structurally become a recipient.
 */

// Declaration order is the display order everywhere (see NOTIFICATION_CATEGORY_OPTIONS).
export enum NotificationCategory {
  SAP_VALIDATION = 'SAP-Pflichtangabe',
  TECHNICAL_ERROR = 'Technischer Fehler',
  AI_REVIEW = 'KI-Rückfrage',
  CUSTOMER_INFO_MISSING = 'Kundenangabe fehlt',
}

export enum NotificationSeverity {
  ERROR = 'Fehler',
  QUESTION = 'Rückfrage',
}

export enum NotificationStatus {
  OPEN = 'Offen',
  ANSWERED = 'Beantwortet',
  RESOLVED = 'Erledigt',
  DISMISSED = 'Verworfen',
}

export enum NotificationChannel {
  DASHBOARD = 'dashboard',
  MAIL = 'mail',
}

/** Delivery lifecycle per channel. `skipped` means the user's preferences ruled the channel out. */
export type NotificationDeliveryState = 'pending' | 'sent' | 'failed' | 'skipped';

/** Whether a patch targets a single position or the extraction's header/company block. */
export type NotificationPatchScope = 'position' | 'header';

export const NOTIFICATION_CATEGORY_OPTIONS = Object.values(NotificationCategory);

/** Open work vs. the archive — the two views the notification lists offer. */
export const NOTIFICATION_OPEN_STATES: string[] = [NotificationStatus.OPEN, NotificationStatus.ANSWERED];
export const NOTIFICATION_DONE_STATES: string[] = [NotificationStatus.RESOLVED, NotificationStatus.DISMISSED];

/**
 * The categories that stop an offer from reaching SAP. They are the ones worth pushing —
 * the badge counts them, and they alone trigger a question mail. An assumption to look over
 * is real work but not urgent: making it ring would make the ringing meaningless.
 */
export const BLOCKING_CATEGORIES: string[] = [
  NotificationCategory.SAP_VALIDATION,
  NotificationCategory.TECHNICAL_ERROR,
];

// Display wordings — decoupled from the enum values, which are persisted verbatim.
export const NOTIFICATION_CATEGORY_LABELS: Record<NotificationCategory, string> = {
  [NotificationCategory.SAP_VALIDATION]: 'Pflichtangabe für SAP fehlt',
  [NotificationCategory.TECHNICAL_ERROR]: 'Technischer Fehler',
  [NotificationCategory.AI_REVIEW]: 'Rückfrage der Extraktion',
  [NotificationCategory.CUSTOMER_INFO_MISSING]: 'Angabe fehlt in der Anfrage',
};

// Shown as a hint under the title, so the four categories cannot be used interchangeably.
export const NOTIFICATION_CATEGORY_DESCRIPTIONS: Record<NotificationCategory, string> = {
  [NotificationCategory.SAP_VALIDATION]:
    'Das Angebot kann nicht an SAP übermittelt werden, solange diese Angaben fehlen.',
  [NotificationCategory.TECHNICAL_ERROR]:
    'Die Verarbeitung ist technisch fehlgeschlagen — die Anfrage liegt sonst unbemerkt liegen.',
  [NotificationCategory.AI_REVIEW]:
    'Die Extraktion war sich nicht sicher und braucht eine Entscheidung, bevor es weitergeht.',
  [NotificationCategory.CUSTOMER_INFO_MISSING]:
    'Die Anfrage selbst lässt eine Angabe offen — bitte beim Kunden erfragen.',
};

// One colour per category, used for the dot in the list and the detail header.
export const NOTIFICATION_CATEGORY_DOTS: Record<NotificationCategory, string> = {
  [NotificationCategory.SAP_VALIDATION]: '#d9534f',
  [NotificationCategory.TECHNICAL_ERROR]: '#7a2620',
  [NotificationCategory.AI_REVIEW]: '#009ee1',
  [NotificationCategory.CUSTOMER_INFO_MISSING]: '#d08a1f',
};

/**
 * A concrete correction an answer maps to. An option that carries a patch is the
 * deterministic short cut — it is applied without ever asking the LLM.
 */
@Entity()
export class NotificationPatch extends AdornisEntity {
  static override _class = 'NotificationPatch';

  static override resolveID = (value: Partial<NotificationPatch>) =>
    value._id ?? `${value.scope ?? ''}:${value.productId ?? ''}:${value.field ?? ''}`;

  @Field(() => ID, { default: v => v ?? A.getGloballyUniqueID() }) _id!: string;
  @Field(() => String) scope!: NotificationPatchScope;
  /** `ExtractedProductInfo._id` of the affected position; unset for header patches. */
  @Field(() => String) productId: Maybe<string>;
  @Field(() => String) field!: string;
  /** Serialised as a string; coerced to the target field's type when applied. */
  @Field(() => String) value: Maybe<string>;

  constructor(doc: PartialEntityData<NotificationPatch>) {
    super(doc);
  }
}

/** One offered answer. `key` is what the user clicks — and what they write back in a mail reply. */
@Entity()
export class NotificationOption extends AdornisEntity {
  static override _class = 'NotificationOption';

  static override resolveID = (value: Partial<NotificationOption>) => value._id ?? value.key ?? '';

  @Field(() => ID, { default: v => v ?? A.getGloballyUniqueID() }) _id!: string;
  @Field(() => String) key!: string;
  @Field(() => String) label!: string;
  @Field(() => NotificationPatch) patch: Maybe<NotificationPatch>;
  /**
   * The option confirms an assumption the extraction already made, so answering with it
   * settles the question without changing anything. Needed because "yes, one piece is right"
   * is a real answer — writing the same value back would show up as a correction that never
   * happened.
   */
  @Field(() => Boolean) confirms: Maybe<boolean>;

  constructor(doc: PartialEntityData<NotificationOption>) {
    super(doc);
  }
}

/** What the LLM made of a free-text answer. Never applied silently below the confidence gate. */
@Entity()
export class AnswerInterpretation extends AdornisEntity {
  static override _class = 'AnswerInterpretation';

  @Field(() => ID, { default: v => v ?? A.getGloballyUniqueID() }) _id!: string;
  @Field(() => Float) confidence: Maybe<number>;
  @Field(() => String) summary: Maybe<string>;
  /**
   * Why the model read the answer the way it did, in its own words. Kept because the reading
   * is the one step in this feature that cannot be re-derived after the fact: the same prompt
   * on the same text may read differently tomorrow. Shown verbatim in the Mailverkehr view.
   */
  @Field(() => String) reasoning: Maybe<string>;
  /** The passage of the reply the reading rests on — so a wrong mapping is visible as one. */
  @Field(() => String) sourceQuote: Maybe<string>;
  @Field(() => [NotificationPatch]) patches: Maybe<NotificationPatch[]>;
  @Field(() => Boolean) applied: Maybe<boolean>;
  @Field(() => Boolean) needsConfirmation: Maybe<boolean>;
  @Field(() => Date) appliedAt: Maybe<Date>;
  @Field(() => String) note: Maybe<string>;

  constructor(doc: PartialEntityData<AnswerInterpretation>) {
    super(doc);
  }
}

@Entity()
export class NotificationAnswer extends AdornisEntity {
  static override _class = 'NotificationAnswer';

  @Field(() => ID, { default: v => v ?? A.getGloballyUniqueID() }) _id!: string;
  @Field(() => String) channel!: NotificationChannel;
  @Field(() => String) selectedOptionKey: Maybe<string>;
  @Field(() => String) freeText: Maybe<string>;
  @Field(() => String) answeredByUserID: Maybe<string>;
  @Field(() => Date) answeredAt: Maybe<Date>;
  /** `MailEntity._id` of the reply mail, when the answer came in over the mail channel. */
  @Field(() => String) sourceMailID: Maybe<string>;
  @Field(() => AnswerInterpretation) interpretation: Maybe<AnswerInterpretation>;
  /**
   * What the answer actually changed, in one readable line — and what it did not.
   *
   * Both were previously only a return value on the way to a snackbar: a refused patch told
   * the person at the screen why, and was then gone. For an answer that arrived by mail there
   * was nobody at a screen, so the reason was lost entirely. The Mailverkehr view reads these.
   */
  @Field(() => String) outcomeSummary: Maybe<string>;
  @Field(() => [String]) rejections: Maybe<string[]>;

  constructor(doc: PartialEntityData<NotificationAnswer>) {
    super(doc);
  }
}

/**
 * One answer on its way in, as the request view submits them: a whole request's questions are
 * answered in one pass, so they also travel in one mutation. Answering them one by one would
 * revalidate and re-sync the request once per question — and could hand the offer to SAP
 * halfway through, before the person was done.
 *
 * Named `…Draft`, not `…Input`: BaseQL derives the name of an entity's GraphQL *input* type by
 * appending "Input" to its `_class` (`getTypeString(type, true)`). An entity called
 * `NotificationAnswerInput` therefore collides head-on with the input type generated for
 * {@link NotificationAnswer}, and the schema refuses to build — at startup, long after tsc and
 * the bundler are happy. No entity in this app may be named `<other entity>Input`.
 */
@Entity()
export class NotificationAnswerDraft extends AdornisEntity {
  static override _class = 'NotificationAnswerDraft';

  @Field(() => ID, { default: v => v ?? A.getGloballyUniqueID() }) _id!: string;
  @Field(() => String) notificationID!: string;
  @Field(() => String) optionKey: Maybe<string>;
  @Field(() => String) freeText: Maybe<string>;
  /**
   * The key of a master record the user picked — a debitor or a material number. Its own field
   * rather than free text, because it must not be read by a model: it is not a description of a
   * record, it *is* one, and running it through an interpretation could only make it worse.
   */
  @Field(() => String) reference: Maybe<string>;

  constructor(doc: PartialEntityData<NotificationAnswerDraft>) {
    super(doc);
  }
}

/** Per-channel delivery record. The mail entry also carries what the inbound matcher needs. */
@Entity()
export class NotificationDelivery extends AdornisEntity {
  static override _class = 'NotificationDelivery';

  static override resolveID = (value: Partial<NotificationDelivery>) => value._id ?? value.channel ?? '';

  @Field(() => ID, { default: v => v ?? A.getGloballyUniqueID() }) _id!: string;
  @Field(() => String) channel!: NotificationChannel;
  @Field(() => String) state!: NotificationDeliveryState;
  @Field(() => Date) sentAt: Maybe<Date>;
  @Field(() => String) error: Maybe<string>;
  /** RFC 5322 `Message-ID` of the question mail, without angle brackets. */
  @Field(() => String) messageID: Maybe<string>;
  /**
   * Every `Message-ID` we have sent about these questions — the question mail and each
   * acknowledgement after it.
   *
   * One id is not enough. The acknowledgement is the last mail in the thread, so replying to
   * *it* is the natural thing to do; matching only the question mail left that reply with no
   * anchor at all, and it fell through to the extraction and was processed as a new customer
   * inquiry. Every mail we send has to be a way back in.
   */
  @Field(() => [String]) threadMessageIDs: Maybe<string[]>;
  /** Subject tag `[#RF-<token>]`, the fallback anchor when a server rewrites `In-Reply-To`. */
  @Field(() => String) replyToken: Maybe<string>;
  @Field(() => Int) sendCount: Maybe<number>;
  @Field(() => Date) lastSentAt: Maybe<Date>;
  @Field(() => Date) confirmationSentAt: Maybe<Date>;

  constructor(doc: PartialEntityData<NotificationDelivery>) {
    super(doc);
  }
}

@Entity()
export class UserNotification extends MongoEntity {
  static override _class = 'UserNotification';
  static override _collectionName = 'user-notifications';

  /** Always an `AdornisUser._id`. There is deliberately no field for a raw address. */
  @Field(() => String) recipientUserID!: string;
  /** Audit only — never a send target; the sender re-reads the address from the user document. */
  @Field(() => String) recipientEmailSnapshot: Maybe<string>;

  @Field(() => String) category!: NotificationCategory;
  @Field(() => String) severity!: NotificationSeverity;
  @Field(() => String, { default: v => v ?? NotificationStatus.OPEN }) status!: NotificationStatus;

  @Field(() => String) mailID: Maybe<string>;
  @Field(() => String) mailProcessingID: Maybe<string>;
  @Field(() => String) positionNumber: Maybe<string>;
  @Field(() => String) productId: Maybe<string>;
  /** Key from PRODUCT_FIELD_LABELS, or a SapFieldIssue.field — the finest-grained reference. */
  @Field(() => String) fieldKey: Maybe<string>;

  @Field(() => String) title!: string;
  /** Which request this belongs to — the mail's subject. Shown under the title in the list. */
  @Field(() => String) contextLabel: Maybe<string>;
  @Field(() => String) body!: string;
  @Field(() => [NotificationOption]) options: Maybe<NotificationOption[]>;
  @Field(() => Boolean, { default: v => v ?? true }) allowsFreeText: Maybe<boolean>;

  @Field(() => Date) readAt: Maybe<Date>;
  @Field(() => NotificationAnswer) answer: Maybe<NotificationAnswer>;
  @Field(() => [NotificationDelivery]) deliveries: Maybe<NotificationDelivery[]>;

  /** Unique. A recurring problem re-opens its notification instead of creating a second one. */
  @Field(() => String) dedupKey!: string;
  @Field(() => Int, { default: v => v ?? 1 }) raiseCount: Maybe<number>;
  @Field(() => Date) lastRaisedAt: Maybe<Date>;
  /** Audit trail, same idea as MailProcessing.notes: what happened to this notification. */
  @Field(() => [String]) history: Maybe<string[]>;

  @Field(() => Date, { default: v => v ?? new Date() }) createdAt: Maybe<Date>;
  @Field(() => Date) updatedAt: Maybe<Date>;

  constructor(doc: PartialEntityData<UserNotification>) {
    super(doc);
  }
}
